// src/components/instructor/OrganizationDashboardShell.js
//
// Themed shell wrapper for the Organisation workspace.
// Adds the motus nav bar + route-driven tab navigation, then renders the active tab's canonical
// content (Overview / Bookings / Team / Clients / Settings / Inbox). The legacy giant organisation
// dashboard page is NO LONGER mounted here (Pass 58F6) — preserved on disk, not imported.
//
// Pass 87.2 — ONE org-scoped operations data session. The shell mounts a single OperationsDataProvider
// (keyed by orgId, so an org switch is a clean remount with no prior-org state), and EVERY tab reads
// from it: Overview → Bookings → Team → Clients no longer each re-fetch the organisation dataset. The
// Bookings schedule renders from the fast current-season feed; all-time data (client history, archived
// seasons) loads lazily on demand. The old per-tab one-shot loaders are gone.

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { loadOrganizationsByIds } from '../../utils/organizationData';
import { useSelectedOrganizationContext } from '../../utils/organizationSelection';
import { OperationsDataProvider, useOperationsData } from '../../hooks/useOperationsData';
import OrgOperationsWorkspace from './OrgOperationsWorkspace';
import OrgClientsWorkspace from './OrgClientsWorkspace';
import OrgOverview from './OrgOverview';
import { resolveBookingsEntry } from '../../utils/operationsWorkspace';
import { zonedShiftDays, zonedDateKey, wallClockToInstant } from '../../utils/opsTime';
import { resolveOrgTimezone, resolveOrgSeasonId } from '../../utils/organizationRuntime';
import InstructorCalendarView from './InstructorCalendarView';
import OrgFinanceView from './OrgFinanceView';
import OrgTeamView from './OrgTeamView';
import OrgAccessSettings from './OrgAccessSettings';
import OrgAreaSettings from './OrgAreaSettings';
import ScheduleVisibilityControl from './ops/ScheduleVisibilityControl';
import CalendarSyncSettings from './ops/CalendarSyncSettings';
import InboxPanel from '../inbox/InboxPanel';
import MotusIcon from '../shared/MotusIcon';

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', card3:'#ECEAE4',
  border:'rgba(0,0,0,0.06)', borderH:'rgba(0,0,0,0.12)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)',
  green:'#22C55E', greenDim:'rgba(34,197,94,0.08)',
  orange:'#F05A1F', orangeDim:'rgba(240,90,31,0.08)',
  purple:'#9333EA', purpleDim:'rgba(147,51,234,0.08)',
  gold:'#D97706', goldDim:'rgba(217,119,6,0.08)',
  red:'#DC2626',
  t1:'#1C1B18', t2:'#6B6B63', t3:'#A3A299',
};
const font = "'DM Sans',sans-serif";
const fontD = "'Bebas Neue',sans-serif";

// Pass 49B §2: ONE primary operations product. "Schedule" is no longer a competing top-level
// tab — Bookings (Bookings & Availability) is the single operations workspace. The old
// `schedule` route still resolves (compatibility) but deep-links into Bookings → Day.
// Pass 58C — HIDE UNTIL REAL: the Finances tab surfaces fabricated figures (commission ~22% estimate,
// WPI 5% deduction with no records, "Net payout" re-rendering gross). It is hidden for the V1 pilot (code
// retained, not deleted) until commission stage + deductions are sourced. Everything else is real.
// Pass 58F3 — Content is an honest "Not enabled" placeholder for the pilot; keep the primary SAS
// Operations nav focused (Overview/Bookings/Team/Clients/Settings) and don't let an unfinished area
// compete for attention in the demo. The /content/drills route and the platform are NOT deleted.
const V1_HIDDEN_TABS = new Set(['finances', 'content']);
// Non-primary tabs reachable by deep-link (e.g. the bell → /org/:orgId/inbox) but not shown in the
// primary tab bar. They must still resolve to real content, not fall back to Overview.
const EXTRA_TABS = new Set(['inbox']);
const ALL_TABS = [
  { key:'overview', label:'Overview', icon:'layers' },
  { key:'bookings', label:'Bookings', icon:'clipboard' },
  { key:'team', label:'Team', icon:'user' },
  { key:'clients', label:'Clients', icon:'target' },
  { key:'finances', label:'Finances', icon:'dollar' },
  { key:'content', label:'Content', icon:'edit' },
  { key:'settings', label:'Settings', icon:'settings' },
];
const TABS = ALL_TABS.filter((t) => !V1_HIDDEN_TABS.has(t.key));

function OrganizationDashboardShell() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  const { userData } = useAuth();
  // Pass 48 §2 — ONE organisation-selection source across every Org tab. Pass 82C.2 §9 — resolve it up-front so all
  // generic tenant navigation (the ?date deep-link, the "Tomorrow" deep-link below) is computed in the SELECTED
  // ORGANIZATION's timezone, never a hard-coded Asia/Tokyo.
  const { selectedOrganizationId, selectedOrganization } = useSelectedOrganizationContext(userData);
  const orgTz = resolveOrgTimezone(selectedOrganization);
  // Pass 87.2 — the org's current season is resolved up-front so the ONE data session can scope its
  // first-render bookings query (seasonId == currentSeasonId) — the single highest-impact perf fix.
  const currentSeasonId = resolveOrgSeasonId(selectedOrganization);
  const tabFromRoute = routeParams.tab || searchParams.get('tab');
  const viewParam = searchParams.get('view');
  const dateParam = searchParams.get('date');
  // Pass 49B §2: the deprecated `schedule` route redirects into Bookings → Day.
  const entry = resolveBookingsEntry(tabFromRoute);
  // Pass 58F4 — the ROUTE is the SINGLE source of truth for the active surface. `tab` is DERIVED from
  // routeParams every render (no stale local state), so /org/:id/inbox actually renders Inbox and
  // browser Back/Forward + hard refresh always land on the surface the URL names. Bookings view/date
  // likewise derive from ?view / ?date so deep-links survive refresh.
  const tab = (TABS.some((t) => t.key === entry.tab) || EXTRA_TABS.has(entry.tab)) ? entry.tab : 'overview';
  const bookingsView = viewParam || entry.view;
  const bookingsDate = dateParam ? wallClockToInstant(dateParam, '00:00', orgTz) : null;

  // §10 REAL replace-navigation: rewrite the deprecated /schedule URL to the canonical
  // Bookings → Day deep-link, so browser Back/Forward and copied URLs match what is shown.
  useEffect(() => {
    if (entry.redirected && routeParams.organizationId) {
      navigate(`/org/${routeParams.organizationId}/bookings?view=day`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.redirected, routeParams.organizationId]);
  const [inspectedInstructor, setInspectedInstructor] = useState(null); // { id, name }
  const orgId = selectedOrganizationId || userData?.orgId || '';
  // Pass 83B.3 §5 — the admin instructor inspector belongs to an organisation; reset it on org switch so an
  // ORG_A instructor context never survives into ORG_B.
  useEffect(() => { setInspectedInstructor(null); }, [orgId]);
  // Pass 58F4 — primary nav + cards NAVIGATE (URL is authoritative); they never setTab a local state.
  const routeOrgId = routeParams.organizationId || orgId;
  const goTab = (key, search = '') => navigate(key === 'overview' ? `/org/${routeOrgId}${search}` : `/org/${routeOrgId}/${key}${search}`);

  // Pass 58F2 — the organisation dashboard leads with the ORGANISATION, not the signed-in person.
  // Resolve the canonical org name from the org record (authoritative), falling back to the membership's
  // organizationName, then the org id. Never fall back to the user's own name (the old bug that made the
  // page read as "John Bridgen / Organization").
  // Pass 83B.3 §4 — the org name is org-specific state: scope-key it so A's name never flashes under B while
  // B resolves, and a late A response cannot overwrite B.
  const [orgNameRec, setOrgNameRec] = useState({ scopeKey: '', name: '' });
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    loadOrganizationsByIds(db, [orgId])
      // Pass 88.1 — prefer the canonical human display label (displayName → name) so the Operations header
      // shows "Voletus Pilot", not the raw org name "Motus Pilot 2". Fall back to '' (not the id) so the
      // existing orgName chain can continue to the membership name; organizationId is unchanged underneath.
      .then((byId) => { if (!cancelled) setOrgNameRec({ scopeKey: orgId, name: byId[orgId]?.displayName || byId[orgId]?.name || '' }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orgId]);
  const orgRecordName = orgNameRec.scopeKey === orgId ? orgNameRec.name : '';
  const orgName = orgRecordName || selectedOrganization?.organizationName || orgId || 'Organisation';
  // Friendly role label for the quiet "Signed in as … · Role" line.
  const ROLE_LABEL = { organization_owner: 'Owner', organization_admin: 'Admin', ops: 'Operations', instructor: 'Instructor' };
  const signedInRole = ROLE_LABEL[selectedOrganization?.role] || '';

  return (
    <div style={{ minHeight:'100vh', background:C.bg, color:C.t1, fontFamily:font }}>
      {/* NAV */}
      {/* Workspace context nav (tabs only — logo/notifications/account live in the GlobalHeader) */}
      <nav className="m-navwrap" style={{ height:52, display:'flex', alignItems:'center', gap:14, padding:'0 36px', borderBottom:`1px solid ${C.border}`, background:'rgba(255,255,255,0.92)', backdropFilter:'blur(18px)', position:'sticky', top:56, zIndex:90 }}>
        {/* §11 workspace context — the canonical organisation name, not a hardcoded "Organisation". */}
        <span style={{ fontSize:11, fontWeight:800, letterSpacing:1, textTransform:'uppercase', color:C.purple, whiteSpace:'nowrap' }}>{orgName} · Operations</span>
        <div className="m-tabrail" style={{ display:'flex', gap:2, background:C.card, border:`1px solid ${C.border}`, borderRadius:11, padding:3, overflowX:'auto' }}>
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => goTab(t.key)} style={{ padding:'6px 14px', borderRadius:8, fontSize:13, fontWeight:active?700:500, color:active?'#FFFFFF':C.t3, border:'none', background:active?C.purple:'none', fontFamily:font, cursor:'pointer', transition:'all .18s', display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
                <MotusIcon name={t.icon} size={13} color={active ? '#FFFFFF' : C.t3} />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Pass 87.2 — ONE data session for the whole workspace. Keyed by orgId so switching organisations
          is a clean remount: no previous-org bookings/members can survive into the new org, and the new
          org seeds instantly from its own in-memory snapshot when returning. */}
      <OperationsDataProvider key={orgId} organizationId={orgId} currentSeasonId={currentSeasonId}>
        <OrgDashboardContent
          tab={tab}
          orgId={orgId}
          orgName={orgName}
          orgTz={orgTz}
          signedInRole={signedInRole}
          userData={userData}
          selectedOrganization={selectedOrganization}
          goTab={goTab}
          navigate={navigate}
          inspectedInstructor={inspectedInstructor}
          setInspectedInstructor={setInspectedInstructor}
          bookingsView={bookingsView}
          bookingsDate={bookingsDate}
        />
      </OperationsDataProvider>
    </div>
  );
}

// Pass 87.2 — the tab content consumes the ONE operations data session. Overview + the Bookings schedule
// use the fast CURRENT-season feed; Team / Clients / Finances / the Overview client count need all-time
// history, so they request the lazily-loaded all-seasons snapshot (progressive — each surface shows its
// own loading state until its data arrives, never a single global gate).
function OrgDashboardContent({ tab, orgId, orgName, orgTz, signedInRole, userData, selectedOrganization, goTab, navigate, inspectedInstructor, setInspectedInstructor, bookingsView, bookingsDate }) {
  const {
    bookings, members, bookingRequests, allBookings,
    bookingsLoading, membersLoading, requestAllBookings,
  } = useOperationsData();

  // Surfaces that need all-time history (archived seasons / full client directory) ask for it lazily.
  const needsAll = tab === 'team' || tab === 'clients' || tab === 'finances' || tab === 'overview';
  useEffect(() => { if (needsAll) requestAllBookings(); }, [needsAll, requestAllBookings]);

  // Progressive readiness — expose `null` (loading) ONLY while a dataset is genuinely empty AND still
  // loading, so the CRM sub-views keep their existing loading semantics without a false "empty" flash.
  const readyCurrent = (bookingsLoading && !bookings.length) ? null : bookings;
  const readyMembers = (membersLoading && !members.length) ? null : members;

  return (
    <main className="m-main" style={{ padding:'32px 36px 80px', maxWidth:1320, margin:'0 auto' }}>

      {/* WELCOME STRIP — Pass 58F5 §16: the large org banner belongs to OVERVIEW only. */}
      {tab === 'overview' && (
      <div className="m-welcome" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:52, height:52, borderRadius:'50%', background:`linear-gradient(135deg,${C.purple},#7C3AED)`, border:'2px solid rgba(147,51,234,0.25)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <MotusIcon name="layers" size={24} color="#FFFFFF" />
          </div>
          <div>
            <div style={{ fontFamily:fontD, fontSize:28, letterSpacing:1.5, color:C.t1, lineHeight:1, marginBottom:5 }}>{orgName}</div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:13, color:C.t2 }}>Operations overview</span>
              {process.env.REACT_APP_USE_EMULATOR === 'true'
                ? <span style={{ fontSize:10, fontWeight:700, color:'#B45309', background:'#FEF3C7', border:'1px solid #F59E0B', borderRadius:5, padding:'1px 7px' }}>● LOCAL · EMULATOR · {orgId}</span>
                : <span style={{ fontSize:10, fontWeight:600, color:'#059669', background:'#ECFDF5', border:'1px solid #A7F3D0', borderRadius:5, padding:'1px 7px' }}>Production</span>}
            </div>
            {userData?.name && (
              <div style={{ fontSize:12, color:C.t3, marginTop:5 }}>Signed in as {userData.name}{signedInRole ? ` · ${signedInRole}` : ''}</div>
            )}
          </div>
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={() => goTab('bookings')} style={{ background:C.purple, color:'#FFFFFF', border:'none', padding:'10px 20px', borderRadius:10, fontFamily:font, fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><MotusIcon name="clipboard" size={14} color="#FFFFFF" /> Bookings</button>
          <button onClick={() => goTab('team')} style={{ background:C.card, border:`1px solid ${C.border}`, color:C.t2, padding:'10px 18px', borderRadius:10, fontFamily:font, fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}><MotusIcon name="user" size={14} color={C.t2} /> Manage Team</button>
        </div>
      </div>
      )}

      {/* TAB CONTENT */}
      {tab === 'overview' && (
        <OrgOverview
          bookings={readyCurrent}
          allBookings={allBookings}
          members={readyMembers}
          cases={bookingRequests}
          onNavigate={(t, view) => {
            if (t === 'bookings') {
              if (view === 'tomorrow') {
                const key = zonedDateKey(zonedShiftDays(new Date(), 1, orgTz), orgTz);
                goTab('bookings', `?view=day&date=${key}`);
              } else {
                goTab('bookings', `?view=${view || 'calendar'}`);
              }
            } else {
              goTab(t);
            }
          }}
        />
      )}

      {/* §2/§16 the Inbox is a utility rendered INSIDE the org shell — the SAS nav above persists. */}
      {tab === 'inbox' && (
        <div>
          <InboxPanel />
        </div>
      )}

      {tab === 'team' && (
        <div>
          <SectionHeader title="Team Management" sub="Manage instructors and staff in your organization" />
          <OrgTeamView bookings={allBookings} members={readyMembers} />
        </div>
      )}

      {tab === 'clients' && (
        <div>
          {/* Pass 50A §14/§15/§17: Clients renders ONLY the client workflow (derived from
              canonical bookings), NOT the giant legacy dashboard. Retention lives inside. */}
          <OrgClientsWorkspace bookings={allBookings || []} members={readyMembers || []} cases={bookingRequests} onOpenRetention={() => navigate('/retention')} />
        </div>
      )}

      {tab === 'bookings' && (
        <div>
          {inspectedInstructor ? (
            <InstructorCalendarView
              instructorId={inspectedInstructor.id}
              instructorName={inspectedInstructor.name}
              orgId={orgId}
              timeZone={orgTz}
              onBack={() => setInspectedInstructor(null)}
            />
          ) : (
            <>
              {/* Pass 49B: the ONE operations workspace (Calendar · Day · Week · Season · Unassigned). */}
              {/* Pass 50A §18: the giant legacy dashboard is NO LONGER embedded under Bookings. */}
              <OrgOperationsWorkspace
                initialView={bookingsView}
                initialDate={bookingsDate}
                onSelectInstructor={(id, name) => setInspectedInstructor({ id, name })}
              />
            </>
          )}
        </div>
      )}

      {tab === 'finances' && (
        <div>
          <OrgFinanceView bookings={allBookings} members={readyMembers} />
        </div>
      )}

      {tab === 'content' && (
        <div>
          <SectionHeader title="Content Library" sub="Drill templates, content packs, and premium content" />
          <div style={{ background:C.card2, border:`1px dashed ${C.borderH}`, borderRadius:18, padding:'40px 32px', textAlign:'center' }}>
            <div style={{ fontFamily:fontD, fontSize:24, letterSpacing:1, color:C.t1, marginBottom:8 }}>Not enabled</div>
            <div style={{ fontSize:14, color:C.t2, lineHeight:1.6, maxWidth:520, margin:'0 auto' }}>
              The content library (drill templates, content packs, premium content) is not enabled for this
              organisation yet. It will appear here when turned on.
            </div>
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <div>
          {/* Pass 58C — Access & Invitations is the FIRST-CLASS settings surface: the single canonical
              place to administer account access (owner/admin only). Team no longer carries a competing
              invite/role/revoke UI — it shows status and links here. */}
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:20, marginBottom:20 }}>
            <OrgAccessSettings members={readyMembers || []} organizationId={orgId} />
          </div>
          {/* Pass 58F6 — the ORGANISATION-LEVEL schedule publication policy (57-series canonical control,
              backed by the deployed updateScheduleVisibilityPolicy callable). Its canonical home is
              Settings; the per-member override lives in Team → member controls. */}
          {orgId ? (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:20 }}>
              <ScheduleVisibilityControl mode="organization" organizationId={orgId} />
            </div>
          ) : null}
          {/* Pass 82E — operating areas / service locations / meeting points configuration (org owner/admin). */}
          {orgId ? (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:20, marginTop:20 }}>
              <OrgAreaSettings organizationId={orgId} organization={selectedOrganization} />
            </div>
          ) : null}
          {/* Pass 88 — Google Calendar connection management (owner/admin). Lazy; booking stays authority. */}
          {orgId ? (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:18, padding:20, marginTop:20 }}>
              <CalendarSyncSettings organizationId={orgId} />
            </div>
          ) : null}
          {/* Pass 58F6 — the legacy giant organisation dashboard page is NO LONGER mounted in Settings.
              Its capabilities are preserved (see docs/LEGACY_CAPABILITY_INVENTORY.md) for deliberate,
              surface-by-surface re-integration — not exposed as an "Advanced (legacy)" dumping ground. */}
        </div>
      )}
    </main>
  );
}

// ── Sub-components ──

function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:24, letterSpacing:1.5, marginBottom:3 }}>{title}</div>
      <div style={{ fontSize:13, color:C.t3 }}>{sub}</div>
    </div>
  );
}



export default OrganizationDashboardShell;
